import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  TextField,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Card,
  CardContent,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Home,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  AttachMoney,
  Visibility,
  Map,
  Image,
  PhotoCamera,
  CheckCircle
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useCurrentUser } from '../../hooks/useApi';
import { Navigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return (
    <div hidden={value !== index} style={{ flex: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
      {value === index && <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>{children}</Box>}
    </div>
  );
}

export const MarketGroupingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = useState(0);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [addMarketsDialogOpen, setAddMarketsDialogOpen] = useState(false);
  const [addUsersDialogOpen, setAddUsersDialogOpen] = useState(false);
  const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false);
  const [updatePriceDialogOpen, setUpdatePriceDialogOpen] = useState(false);
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [newMonthlyPrice, setNewMonthlyPrice] = useState('');
  const [selectedMarkets, setSelectedMarkets] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Image management state
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [generateImageDialogOpen, setGenerateImageDialogOpen] = useState(false);
  const [selectImageDialogOpen, setSelectImageDialogOpen] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageGenerationOperationId, setImageGenerationOperationId] = useState<string | null>(null);
  const [imageGenerationProgress, setImageGenerationProgress] = useState<string>('');

  // Fetch grouping details from private-db
  const { data: groupingData, isLoading: groupingLoading } = useQuery({
    queryKey: ['customization-grouping', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/customization-groupings/${id}/details`);
      if (!response) throw new Error('No response from server');
      if (!response.ok) throw new Error('Failed to fetch grouping');
      return response.json();
    },
    enabled: !!user && !!id
  });

  // Fetch all markets for selection
  const { data: allMarketsData } = useQuery({
    queryKey: ['all-markets'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/marketcompass/admin/markets/all');
      if (!response) throw new Error('No response from server');
      if (!response.ok) throw new Error('Failed to fetch markets');
      return response.json();
    },
    enabled: !!user
  });

  // Fetch all users for selection (store.privileged users)
  const { data: allUsersData } = useQuery({
    queryKey: ['privileged-users'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/portal-admin/users?email_domain=horwathhtl.com');
      if (!response) throw new Error('No response from server');
      if (!response.ok) throw new Error('Failed to fetch users');
      return response.json();
    },
    enabled: !!user
  });

  // Fetch available images for this grouping
  const { data: availableImagesData, isLoading: imagesLoading } = useQuery({
    queryKey: ['grouping-available-images', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/customization-groupings/${id}/available-images`);
      if (!response) throw new Error('No response from server');
      if (!response.ok) throw new Error('Failed to fetch available images');
      return response.json();
    },
    enabled: !!user && !!id
  });

  // Poll operation status for image generation
  const { data: operationData } = useQuery({
    queryKey: ['image-generation-operation', imageGenerationOperationId],
    queryFn: async () => {
      if (!imageGenerationOperationId) return null;
      const response = await authenticatedFetch(`/api/operations/${imageGenerationOperationId}`);
      if (!response) throw new Error('No response from server');
      if (!response.ok) throw new Error('Failed to fetch operation status');
      return response.json();
    },
    enabled: !!imageGenerationOperationId && !!user,
    refetchInterval: (query) => {
      // Stop polling if operation is completed or failed
      const data = query.state.data;
      if (!data?.success) return false;
      const status = data.operation?.status;
      if (status === 'completed' || status === 'error') return false;
      return 2000; // Poll every 2 seconds
    }
  });

  // Update grouping mutation
  const updateMutation = useMutation({
    mutationFn: async (data: { name?: string; description?: string }) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/customization-groupings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update grouping');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setEditingInfo(false);
      setSuccess('Grouping updated successfully');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Add markets mutation
  const addMarketsMutation = useMutation({
    mutationFn: async (marketIds: number[]) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/market-groupings/${id}/markets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ market_ids: marketIds })
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add markets');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setAddMarketsDialogOpen(false);
      setSelectedMarkets([]);
      setSuccess('Markets added successfully');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Remove market mutation
  const removeMarketMutation = useMutation({
    mutationFn: async (marketId: number) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/market-groupings/${id}/markets/${marketId}`, {
        method: 'DELETE'
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove market');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setSuccess('Market removed successfully');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Add users visibility mutation
  const addUsersMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/market-groupings/${id}/visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: userIds })
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add users');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setAddUsersDialogOpen(false);
      setSelectedUsers([]);
      setSuccess('User visibility added successfully');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Remove user visibility mutation
  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/market-groupings/${id}/visibility/${userId}`, {
        method: 'DELETE'
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove user');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setSuccess('User visibility removed successfully');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Create Stripe product mutation
  const createProductMutation = useMutation({
    mutationFn: async (price: number) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/market-groupings/${id}/create-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grouping_id: parseInt(id!), monthly_price: price })
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create product');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setCreateProductDialogOpen(false);
      setMonthlyPrice('');
      setSuccess('Stripe product created successfully. Syncing via webhooks...');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Update price mutation
  const updatePriceMutation = useMutation({
    mutationFn: async (price: number) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/market-groupings/${id}/update-price`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_monthly_price: price })
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update price');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setUpdatePriceDialogOpen(false);
      setNewMonthlyPrice('');
      setSuccess('Price updated successfully. Syncing via webhooks...');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Update product image mutation
  const updateImageMutation = useMutation({
    mutationFn: async (imageUrl: string | null) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/customization-groupings/${id}/image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_image_url: imageUrl })
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update image');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-grouping', id] });
      setSuccess('Product image updated successfully');
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Generate image mutation
  const generateImageMutation = useMutation({
    mutationFn: async (prompt: string) => {
      const response = await authenticatedFetch(`/api/marketcompass/admin/customization-groupings/${id}/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start image generation');
      }
      return response.json();
    },
    onSuccess: (data) => {
      // API now returns 202 with operationId
      if (data.operationId) {
        setImageGenerationOperationId(data.operationId);
        setImageGenerationProgress('Starting image generation...');
      }
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
      setImageGenerationOperationId(null);
      setImageGenerationProgress('');
    }
  });

  // Handle operation status updates
  React.useEffect(() => {
    if (!operationData || !operationData.success) return;

    const operation = operationData.operation;
    const status = operation.status;

    // Update progress text
    if (operation.progress_text) {
      setImageGenerationProgress(operation.progress_text);
    }

    // Handle completion
    if (status === 'completed') {
      const imageUrl = operation.result?.url;
      if (imageUrl) {
        setSelectedImageUrl(imageUrl);
        queryClient.invalidateQueries({ queryKey: ['grouping-available-images', id] });
        setSuccess('Image generated successfully! Select it below to use.');
        setTimeout(() => setSuccess(null), 5000);
      }
      setGenerateImageDialogOpen(false);
      setImagePrompt('');
      setImageGenerationOperationId(null);
      setImageGenerationProgress('');
    }

    // Handle error
    if (status === 'error') {
      setError(operation.error_message || 'Image generation failed');
      setTimeout(() => setError(null), 5000);
      setImageGenerationOperationId(null);
      setImageGenerationProgress('');
    }
  }, [operationData, queryClient, id]);

  if (userLoading || groupingLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const grouping = groupingData?.grouping;
  if (!grouping) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Market grouping not found</Alert>
      </Container>
    );
  }

  // Initialize edit fields
  if (editingInfo && !editedName) {
    setEditedName(grouping.name);
    setEditedDescription(grouping.description || '');
  }

  const marketsColumns: GridColDef[] = [
    { field: 'name', headerName: 'Market Name', flex: 1 },
    { field: 'country_name', headerName: 'Country', width: 150 },
    { field: 'continent_name', headerName: 'Continent', width: 150 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value ? 'Active' : 'Inactive'} color={params.value ? 'success' : 'default'} size="small" />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => {
            if (window.confirm(`Remove ${params.row.name} from this grouping?`)) {
              removeMarketMutation.mutate(params.row.id);
            }
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )
    }
  ];

  const usersColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          color="error"
          onClick={() => {
            if (window.confirm(`Remove visibility for ${params.row.email}?`)) {
              removeUserMutation.mutate(params.row.id);
            }
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )
    }
  ];

  const availableMarkets = (allMarketsData?.markets || []).filter(
    (m: any) => !grouping.markets.some((gm: any) => gm.id === m.id)
  );

  const availableUsers = (allUsersData?.data || []).filter(
    (u: any) => !grouping.visible_users.some((vu: any) => vu.id === u.id)
  );

  const currentPrice = grouping.products[0]?.unit_amount ? grouping.products[0].unit_amount / 100 : 0;

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link color="inherit" onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <Home sx={{ mr: 0.5 }} fontSize="inherit" /> Home
          </Link>
          <Link color="inherit" onClick={() => navigate('/admin/hub')} sx={{ cursor: 'pointer' }}>Admin Hub</Link>
          <Link color="inherit" onClick={() => navigate('/admin/market-groupings')} sx={{ cursor: 'pointer' }}>Market Groupings</Link>
          <Typography color="text.primary">{grouping.name}</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/market-groupings')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">Market Grouping: {grouping.name}</Typography>
          <Chip label={grouping.is_active ? 'Active' : 'Inactive'} color={grouping.is_active ? 'success' : 'default'} />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Paper sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 300px)' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Overview" />
            <Tab label={`Markets (${grouping.market_count})`} />
            <Tab label={`Visibility (${grouping.visible_user_count})`} />
            <Tab label={`Stripe Product${grouping.product_count > 0 ? ` ($${currentPrice}/mo)` : ''}`} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                {!editingInfo ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">Grouping Information</Typography>
                      <Button startIcon={<Edit />} onClick={() => setEditingInfo(true)}>Edit</Button>
                    </Box>
                    <Typography variant="body1" gutterBottom><strong>Name:</strong> {grouping.name}</Typography>
                    <Typography variant="body1"><strong>Description:</strong> {grouping.description || 'No description'}</Typography>
                  </>
                ) : (
                  <>
                    <TextField fullWidth label="Name" value={editedName} onChange={(e) => setEditedName(e.target.value)} sx={{ mb: 2 }} />
                    <TextField fullWidth multiline rows={3} label="Description" value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => updateMutation.mutate({ name: editedName, description: editedDescription })}
                        disabled={updateMutation.isPending}
                      >
                        Save
                      </Button>
                      <Button startIcon={<Cancel />} onClick={() => setEditingInfo(false)}>Cancel</Button>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Product Image Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Product Image</Typography>
                {grouping.product_image_url ? (
                  <Box
                    component="img"
                    src={grouping.product_image_url}
                    alt="Current product image"
                    sx={{
                      width: '100%',
                      maxWidth: '600px',
                      height: 'auto',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      display: 'block',
                      mb: 2
                    }}
                  />
                ) : (
                  <Typography color="text.secondary" sx={{ mb: 2 }}>No product image set. Select an existing image or generate a new one.</Typography>
                )}

                {/* Image Actions */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Image />}
                    onClick={() => setSelectImageDialogOpen(true)}
                  >
                    Select Existing Image
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    onClick={() => setGenerateImageDialogOpen(true)}
                    disabled={!!imageGenerationOperationId}
                  >
                    Generate New Image
                  </Button>
                </Box>

                {selectedImageUrl && selectedImageUrl !== grouping.product_image_url && (
                  <Alert severity="info">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      You have selected a new image. Click the button below to save it as the product image.
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Save />}
                      onClick={() => {
                        updateImageMutation.mutate(selectedImageUrl);
                        setSelectedImageUrl(null);
                      }}
                      disabled={updateImageMutation.isPending}
                      sx={{ mb: 2 }}
                    >
                      Save Selection
                    </Button>
                    <Box
                      component="img"
                      src={selectedImageUrl}
                      alt="Selected image preview"
                      sx={{
                        width: '100%',
                        maxWidth: '600px',
                        height: 'auto',
                        objectFit: 'contain',
                        border: '2px solid #1976d2',
                        borderRadius: 1,
                        display: 'block',
                        mt: 2
                      }}
                    />
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={() => setAddMarketsDialogOpen(true)} disabled={availableMarkets.length === 0}>
                Add Markets
              </Button>
            </Box>
            <DataGrid
              rows={grouping.markets}
              columns={marketsColumns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={() => setAddUsersDialogOpen(true)}>
                Add Users
              </Button>
            </Box>
            <DataGrid
              rows={grouping.visible_users}
              columns={usersColumns}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {grouping.product_count === 0 ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>No Stripe Product</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create a Stripe product to make this grouping available for purchase.</Typography>
                  <Button variant="contained" startIcon={<AttachMoney />} onClick={() => setCreateProductDialogOpen(true)}>Create Stripe Product</Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Stripe Product</Typography>
                  <Typography variant="body1" gutterBottom><strong>Product:</strong> {grouping.products[0].name}</Typography>
                  <Typography variant="body1" gutterBottom><strong>Current Price:</strong> ${currentPrice}/{grouping.products[0].billing_interval || 'month'}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom><strong>Stripe Product ID:</strong> {grouping.products[0].stripe_product_id}</Typography>
                  {grouping.products[0].stripe_price_id && (
                    <Typography variant="body2" color="text.secondary" gutterBottom><strong>Stripe Price ID:</strong> {grouping.products[0].stripe_price_id}</Typography>
                  )}
                  <Button variant="contained" sx={{ mt: 2 }} startIcon={<Edit />} onClick={() => setUpdatePriceDialogOpen(true)}>Update Price</Button>
                </CardContent>
              </Card>
            )}
          </TabPanel>
        </Paper>

        {/* Add Markets Dialog */}
        <Dialog open={addMarketsDialogOpen} onClose={() => setAddMarketsDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Markets</DialogTitle>
          <DialogContent>
            <Autocomplete
              multiple
              options={availableMarkets}
              getOptionLabel={(option: any) => `${option.name} (${option.country_name})`}
              value={selectedMarkets}
              onChange={(_, newValue) => setSelectedMarkets(newValue)}
              renderInput={(params) => <TextField {...params} label="Select Markets" />}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddMarketsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => addMarketsMutation.mutate(selectedMarkets.map((m: any) => m.id))}
              variant="contained"
              disabled={selectedMarkets.length === 0 || addMarketsMutation.isPending}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Users Dialog */}
        <Dialog open={addUsersDialogOpen} onClose={() => setAddUsersDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add User Visibility</DialogTitle>
          <DialogContent>
            <Autocomplete
              multiple
              options={availableUsers}
              getOptionLabel={(option: any) => `${option.name || option.email} (${option.email})`}
              value={selectedUsers}
              onChange={(_, newValue) => setSelectedUsers(newValue)}
              renderInput={(params) => <TextField {...params} label="Select Users" />}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddUsersDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => addUsersMutation.mutate(selectedUsers.map((u: any) => u.id))}
              variant="contained"
              disabled={selectedUsers.length === 0 || addUsersMutation.isPending}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Product Dialog */}
        <Dialog open={createProductDialogOpen} onClose={() => setCreateProductDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Stripe Product</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Monthly subscription only. Enter the monthly price in dollars.
            </Typography>
            <TextField
              fullWidth
              label="Monthly Price (USD)"
              type="number"
              value={monthlyPrice}
              onChange={(e) => setMonthlyPrice(e.target.value)}
              InputProps={{ startAdornment: '$' }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateProductDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createProductMutation.mutate(parseFloat(monthlyPrice))}
              variant="contained"
              disabled={!monthlyPrice || parseFloat(monthlyPrice) <= 0 || createProductMutation.isPending}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Price Dialog */}
        <Dialog open={updatePriceDialogOpen} onClose={() => setUpdatePriceDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Price</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current price: ${currentPrice}/month. Enter new monthly price in dollars.
            </Typography>
            <TextField
              fullWidth
              label="New Monthly Price (USD)"
              type="number"
              value={newMonthlyPrice}
              onChange={(e) => setNewMonthlyPrice(e.target.value)}
              InputProps={{ startAdornment: '$' }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpdatePriceDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => updatePriceMutation.mutate(parseFloat(newMonthlyPrice))}
              variant="contained"
              disabled={!newMonthlyPrice || parseFloat(newMonthlyPrice) <= 0 || updatePriceMutation.isPending}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Generate Image Dialog */}
        <Dialog open={generateImageDialogOpen} onClose={() => !imageGenerationOperationId && setGenerateImageDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Generate New Image with AI</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Describe the image you want to generate. The AI will create a 950x720 pixel image based on your description.
              This process may take 60-120 seconds to complete.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Example prompts:</strong>
              <ul>
                <li>"A stunning cityscape of Paris with the Eiffel Tower at sunset"</li>
                <li>"Modern hotel lobby with elegant furniture and warm lighting"</li>
                <li>"Aerial view of a tropical beach resort with crystal blue water"</li>
              </ul>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Image Description"
              placeholder="Describe the image you want to generate..."
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              disabled={!!imageGenerationOperationId}
              sx={{ mt: 1 }}
            />
            {imageGenerationOperationId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  {imageGenerationProgress || 'Generating image... This may take up to 2 minutes. Please wait.'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateImageDialogOpen(false)} disabled={!!imageGenerationOperationId}>
              Cancel
            </Button>
            <Button
              onClick={() => generateImageMutation.mutate(imagePrompt)}
              variant="contained"
              startIcon={<PhotoCamera />}
              disabled={!imagePrompt.trim() || imagePrompt.trim().length < 10 || !!imageGenerationOperationId}
            >
              {imageGenerationOperationId ? 'Generating...' : 'Generate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Select Existing Image Dialog */}
        <Dialog open={selectImageDialogOpen} onClose={() => setSelectImageDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Select Existing Image</DialogTitle>
          <DialogContent>
            {imagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : availableImagesData?.images?.length === 0 ? (
              <Alert severity="info">
                No images available yet. Generate a new image or add markets with images to this grouping.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 2,
                  mt: 2
                }}
              >
                {availableImagesData?.images?.map((image: any, index: number) => (
                  <Card
                    key={index}
                    sx={{
                      cursor: 'pointer',
                      border: selectedImageUrl === image.url ? '3px solid #1976d2' : '1px solid #e0e0e0',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: 3
                      }
                    }}
                    onClick={() => setSelectedImageUrl(image.url)}
                  >
                    {selectedImageUrl === image.url && (
                      <CheckCircle
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: '#1976d2',
                          bgcolor: 'white',
                          borderRadius: '50%'
                        }}
                      />
                    )}
                    <Box
                      component="img"
                      src={image.url}
                      alt={image.source}
                      sx={{
                        width: '100%',
                        height: 150,
                        objectFit: 'cover'
                      }}
                    />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {image.source}
                      </Typography>
                      <Chip
                        label={image.sourceType}
                        size="small"
                        color={image.sourceType === 'market' ? 'primary' : 'secondary'}
                        sx={{ mt: 0.5 }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectImageDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => setSelectImageDialogOpen(false)}
              variant="contained"
              disabled={!selectedImageUrl}
            >
              Select Image
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
